import { addBreadcrumb, captureMessage } from '@sentry/react';

import { captureException } from 'lib/reporting';
import { createCanvas } from 'lib/utils/canvas';
import { Rect } from 'verify/lib/utils/image';

/**
 * Extracts a frame from a video and draws it to a canvas for later use.
 *
 * This version of the function is used for the redesigned BaseScanner.
 * It assumes the capture window is the same dimensions as the client dimensions
 * of the video element (in practice they are extremely close) and that
 * the video element is object-fit: cover with object-positioning: 50% 50%
 *
 * NOTE: drawing video can throw a transient NS_ERROR_NOT_AVAILABLE error in some browsers.
 */
export function bufferImageWithOverlayWindow({
  source,
  canvasBuffer,
}: {
  source: HTMLVideoElement;
  canvasBuffer: HTMLCanvasElement;
}): void {
  // size of the video stream
  const sourceWidth = source.videoWidth;
  const sourceHeight = source.videoHeight;
  const sourceAspectRatio = sourceWidth / sourceHeight;

  // size of the HTML element containing the video
  // corresponds to the area we want to capture
  const videoContainerWidth = source.clientWidth;
  const videoContainerHeight = source.clientHeight;
  const videoContainerAspectRatio = videoContainerWidth / videoContainerHeight;

  // ratio of screen dimensions to video dimensions
  // needed to translate between capture window size (in screen dimensions)
  // to actual video stream size
  let scaleFactor: number;

  // these are in video stream dimensions
  // how far offset from the top left corner the video stream we want to start capturing
  let offsetX: number;
  let offsetY: number;

  // how much of the video stream we should capture
  let extractedWidth: number;
  let extractedHeight: number;

  if (videoContainerAspectRatio > sourceAspectRatio) {
    // container is wider than source
    // video is taller than container
    // video width is scaled to match container width and is centered vertically
    scaleFactor = sourceWidth / videoContainerWidth;

    offsetX = 0;
    // this is garunteed to be > 0 because
    // videoContainerHeight * scaleFactor
    //         = videoContainerHeight / videoContainerWidth * sourceWidth
    //         = 1 / videoContainerAspectRatio * sourceWidth
    //         < 1 / sourceAspectRatio * sourceWidth = sourceHeight;
    offsetY = Math.floor((sourceHeight - videoContainerHeight * scaleFactor) / 2);
    extractedWidth = sourceWidth;
    // again, this is garuanteed to be less than sourceHeight by the above inequality
    // videoContainerHeight * scaleFactor < sourceHeight
    extractedHeight = Math.floor(videoContainerHeight * scaleFactor);
  } else {
    // container is narrower than source
    // video is wider than container
    // video height is scaled to match container height and is centered horizontally
    scaleFactor = sourceHeight / videoContainerHeight;

    offsetY = 0;
    // this is garunteed to be > 0 because
    // videoContainerWidth * scaleFactor
    //         = videoContainerWidth / videoContainerHeight * sourceHeight
    //         = videoContainerAspectRatio * sourceHeight
    //         <= sourceAspectRatio * sourceHeight = sourceWidth;
    offsetX = Math.floor((sourceWidth - videoContainerWidth * scaleFactor) / 2);
    extractedHeight = sourceHeight;
    // again, this is garuanteed to be less than sourceWidth by the above inequality
    // videoContainerWidth * scaleFactor <= sourceWidth
    extractedWidth = Math.floor(videoContainerWidth * scaleFactor);
  }

  canvasBuffer.width = extractedWidth;
  canvasBuffer.height = extractedHeight;

  /**
   * https://pqina.nl/blog/canvas-area-exceeds-the-maximum-limit/
   * on iOS Safari, there is a max size for canvases.
   * If `getContext` is called on an overly large canvas, we get an `InvalidStateError`.
   */
  try {
    addBreadcrumb({
      message: 'bufferImageWithOverlayWindowNew',
      data: {
        offsetX,
        offsetY,
        extractedHeight,
        extractedWidth,
        sourceHeight,
        sourceWidth,
        videoContainerHeight,
        videoContainerWidth,
        totalPixels: extractedHeight * extractedWidth,
      },
    });

    if (offsetX < 0 || offsetY < 0) {
      captureMessage('capture offset is negative', 'warning');
    }

    if (extractedWidth > sourceWidth || extractedHeight > sourceHeight) {
      captureMessage('capture window dimensions > source dimensions', 'warning');
    }

    const canvasContext = canvasBuffer.getContext('2d', { willReadFrequently: true });
    if (canvasContext == null) {
      throw new Error('No canvas context');
    }

    /**
     * UPDATE: We changed this back to a less noticeable color because this was causing customers to
     * notice the line. We established that transparent pixels were indeed the cause.
     *
     * This color is a bright red. Why do we fill the canvas with it before drawing the image on top?
     *
     * While reviewing data in a fraud audit, we ran into ID captures with very thin black line on
     * one edge of the image. Around 5% of the sample presented this thin black line. This could be
     * evidence of supsicious behaviour or, fraud; say, using a fake video-feed for example, or it could
     * be us adding the line.
     *
     * We were able to repro the issue on the office Samsung Galaxy A10. THe cause was the capture window dimensions
     * Being slightly off from the actual video HTML element dimensions, causing us to want to extract
     * a larger width or height than was available. This led to negative offsetX or Y leading to transparent
     * pixels being extracted (revealing the underlying fill)
     *
     * The fix here is to assume the actualy video HTML dimensions are accurate to what we want to extract
     * So we should not run into this issue again
     */
    canvasContext.fillStyle = `#111111`;
    canvasContext.fillRect(0, 0, canvasBuffer.width, canvasBuffer.height);
    canvasContext.drawImage(
      source,
      // add this to ensure non-negative numbers in case of floating point rounding issues caused by subtraction
      Math.max(offsetX, 0),
      Math.max(offsetY, 0),
      extractedWidth,
      extractedHeight,
      0,
      0,
      extractedWidth,
      extractedHeight,
    );
  } catch (e) {
    captureException(e as Error, 'warning');
    // We decided to absorb this exception: https://github.com/persona-id/persona-web/issues/41906#issuecomment-2157560962
  }
}

/**
 * Extracts a frame from a video and draws it to a canvas for later use.
 *
 * This version of the function is used for the pre-redesign BaseScanner.
 *
 * NOTE: drawing video can throw a transient NS_ERROR_NOT_AVAILABLE error in some browsers.
 */
export function bufferHorizontalImage({
  source,
  precreatedCanvas,
  zoomFactor = 1,
}: {
  source: CanvasImageSource;
  precreatedCanvas: HTMLCanvasElement;
  zoomFactor?: number;
}): HTMLCanvasElement {
  // TODO: precreatedCanvas should never be null.
  if (!precreatedCanvas) captureMessage('canvas unexpectedly null when buffering image', 'warning');
  // See if we can remove the fallback. If not, ensure created canvases are cleaned up.
  const canvasBuffer = precreatedCanvas || createCanvas()[0];

  let sourceWidth: number;
  let sourceHeight: number;
  if (source instanceof HTMLVideoElement) {
    sourceWidth = source.videoWidth;
    sourceHeight = source.videoHeight;
  } else {
    // It's unfortunate that I had to add `as any` here, but I didn't want to increase the
    // scope of this commit: https://github.com/persona-id/persona-web/issues/41906#issuecomment-2157560934
    sourceWidth = (source as any).width as number;
    sourceHeight = (source as any).height as number;
  }

  const newWidth = sourceWidth / zoomFactor;
  const newHeight = Math.min((newWidth * 3) / 4, sourceHeight);
  canvasBuffer.width = newWidth;
  canvasBuffer.height = newHeight;

  const canvasContext = canvasBuffer.getContext('2d', { willReadFrequently: true });
  // It seems like sometimes canvasContext can be null, predominantly on iOS. Seems to occur sporadically.
  // https://sentry.io/organizations/persona-identities-inc/issues/2282621831/events/336a0fd839c4406c9ae2bdeabfcc8164/?project=5579484
  canvasContext?.clearRect(0, 0, canvasBuffer.width, canvasBuffer.height);
  canvasContext?.drawImage(
    source,
    Math.floor(-sourceWidth / 2 + newWidth / 2),
    Math.floor(-sourceHeight / 2 + newHeight / 2),
    sourceWidth,
    sourceHeight,
  );

  return canvasBuffer;
}

/**
 * @returns the ratio between the source video size and the preview size.
 * Eg. A result of 4 means that the video size is 4x the size of the preview.
 */
function calculatePreviewScaleFactor(source: HTMLVideoElement) {
  const sourceWidth = source.videoWidth;
  const sourceHeight = source.videoHeight;

  const sourceAspectRatio = sourceWidth / sourceHeight;

  let scaleFactor: number;

  const videoContainerWidth = source.clientWidth;
  const videoContainerHeight = source.clientHeight;

  const videoContainerAspectRatio = videoContainerWidth / videoContainerHeight;

  if (videoContainerAspectRatio > sourceAspectRatio) {
    // container is wider than source
    scaleFactor = sourceWidth / videoContainerWidth;
  } else {
    // container is narrower than source
    scaleFactor = sourceHeight / videoContainerHeight;
  }

  return scaleFactor;
}

/**
 * Calculates what portion of the raw video feed is in the view finder rect.
 * @param cameraFeedElement The element that the camera feed is drawn to.
 * @param viewFinderElement The view finder element to calculate for.
 * @param overlayElement The element containing the entire overlay. Required
 * to calculate the scale factor of the preview vs the raw camera feed.
 * @returns The dimentions and position of the view finder rect within the
 * raw video frame.
 */
export function calculateViewfinderRegionInCanvas(
  videoElement: HTMLVideoElement,
  cameraFeedElement: HTMLDivElement,
  viewFinderElement: HTMLDivElement,
): Rect {
  const viewfinderRect = viewFinderElement.getBoundingClientRect();
  const captureWindowRect = cameraFeedElement.getBoundingClientRect();
  const scaleFactor = calculatePreviewScaleFactor(videoElement);
  const relX = Math.floor(viewfinderRect.x - captureWindowRect.x);
  const relY = Math.floor(viewfinderRect.y - captureWindowRect.y);

  return {
    x: Math.floor(relX * scaleFactor),
    y: Math.floor(relY * scaleFactor),
    // We floor the viewfinderRect's width and height to get the pixel size of the view finder.
    // This is to get a more accurate representation of what the user sees.
    width: Math.floor(Math.floor(viewfinderRect.width) * scaleFactor),
    height: Math.floor(Math.floor(viewfinderRect.height) * scaleFactor),
  };
}

/*
 * We sometimes see blank captures when the device has resource issues.
 *
 * This filesize was chosen empirically based on manually examining the black images.
 */
export const BLANK_CAPTURE_BYTE_SIZE = 10000;
